/**
 * ResourcesTable — every engineering resource in one list: NA, SA, EU, MEA,
 * employees and contractors alike.
 *
 * The four schedule columns are NOT typed here. They are read off the Manpower
 * calendar (utils/resourceSchedule), so this view and the board can never
 * disagree — put someone on a job in the calendar and they appear here on the
 * same refresh. That is the whole reason it is a derived list rather than
 * another table to maintain.
 *
 * "Future project" is the next piece of WORK, so a block of PTO between two
 * jobs is skipped rather than reported as what they are doing next.
 *
 * Layout (revisions 2, 10/2/26): Region and Employment type are tags beside
 * the name, not columns of their own - they describe the person, not the
 * schedule. Location sits next to Current project: the calendar writes the
 * block's Location onto the card, so the two read together. The Region and
 * Type filter chips stay. Discipline stays (Sharif kept it).
 *
 * 10/16/26 (Sharif): the engineer cards now show only internal engineers and
 * interns, so this is where EVERYONE is - and the page is split in half:
 * internal engineers, interns, MENA and Europe (only when someone is in it)
 * on the left; South Africa on the top
 * right and contractors on the bottom right (data/resourceGroups.js says who
 * is which, from Region and Employment). Below 1100 px the halves stack.
 * A name opens that engineer's page, cards or not.
 */
import { useMemo, useState } from 'react';
import { fmtDate } from '../../utils/format';
import { resourceSchedule } from '../../utils/resourceSchedule';
import { disciplineLabel } from '../../data/disciplines';
import { RESOURCE_GROUPS, resourceGroupOf } from '../../data/resourceGroups';

const GREEN = '#1B5E20';
const TH = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 9,
  fontWeight: 800,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: '#5A6557',
  background: '#F4F6F0',
  borderBottom: `2px solid ${GREEN}`,
  whiteSpace: 'nowrap',
};
const TD = { padding: '8px 10px', fontSize: 11, color: '#1a1a1a', borderBottom: '1px solid #F2F2F2', verticalAlign: 'top' };

const REGION_TONE = {
  NA: { bg: '#E8F5E9', fg: '#1B5E20' },
  SA: { bg: '#FFF3E0', fg: '#E65100' },
  EU: { bg: '#E3F2FD', fg: '#1565C0' },
  MEA: { bg: '#F3E5F5', fg: '#6A1B9A' },
};

// Employee is the plain grey default; the other two stand out because they
// change how someone is counted (interns are left out of utilization).
const EMPLOYMENT_TONE = {
  Contractor: { bg: '#FFF8E1', fg: '#8D6E00' },
  Intern: { bg: '#E0F2F1', fg: '#00695C' },
};
const EMPLOYMENT = ['Employee', 'Contractor', 'Intern'];
// 'intern' and 'Intern' are one type: the chip filter and the tag colour
// compare the canonical spelling.
const employmentOf = (e) => {
  const raw = String(e.employmentType || e.employment_type || '').trim();
  return EMPLOYMENT.find((t) => t.toLowerCase() === raw.toLowerCase()) || raw || 'Employee';
};

function Tag({ text, tone }) {
  const t = tone || { bg: '#F2F2F2', fg: '#666' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 9,
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: 0.5,
        background: t.bg,
        color: t.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
}

/** A project + its dates, or a clear dash. */
function Assignment({ run }) {
  if (!run) return <span style={{ color: '#C9C9C9' }}>-</span>;
  return (
    <>
      <div style={{ fontWeight: 700 }}>{run.project}</div>
      <div style={{ fontSize: 9.5, color: '#888', marginTop: 1 }}>
        {fmtDate(run.start)} → {fmtDate(run.end)}
      </div>
    </>
  );
}

export default function ResourcesTable({ engineers, edits, onOpen }) {
  const [region, setRegion] = useState('All');
  const [kind, setKind] = useState('All');

  const rows = useMemo(
    () =>
      (engineers || [])
        .map((e) => ({
          ...e,
          region: e.region || '-',
          employment: employmentOf(e),
          group: resourceGroupOf(e),
          ...resourceSchedule(e.name, edits || {}),
        }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [engineers, edits]
  );

  const regions = useMemo(() => ['All', ...[...new Set(rows.map((r) => r.region))].filter((r) => r && r !== '-').sort()], [rows]);
  const filtering = region !== 'All' || kind !== 'All';
  const shown = rows.filter((r) => (region === 'All' || r.region === region) && (kind === 'All' || r.employment === kind));

  const chip = (active) => ({
    padding: '4px 11px',
    borderRadius: 14,
    fontSize: 10,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Montserrat',
    border: `1px solid ${active ? GREEN : '#DDE4D8'}`,
    background: active ? GREEN : '#fff',
    color: active ? '#fff' : '#5A6557',
  });

  // While a filter is on, a group with nobody left in it is left out.
  const section = (g) => {
    const list = shown.filter((r) => r.group === g.key);
    if ((filtering || g.hideEmpty) && list.length === 0) return null;
    return <ResourceSection key={g.key} group={g} rows={list} onOpen={onOpen} />;
  };

  return (
    <div style={{ fontFamily: 'Montserrat' }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.2, textTransform: 'uppercase' }}>Region</span>
        {regions.map((r) => (
          <button key={r} type="button" onClick={() => setRegion(r)} style={chip(region === r)}>
            {r}
          </button>
        ))}
        <span style={{ fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.2, textTransform: 'uppercase', marginLeft: 10 }}>
          Type
        </span>
        {['All', ...EMPLOYMENT].map((t) => (
          <button key={t} type="button" onClick={() => setKind(t)} style={chip(kind === t)}>
            {t}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#999' }}>
          {shown.length} of {rows.length} · current and future work read from the calendar
        </span>
      </div>

      <div className="resources-split" data-testid="resources-split">
        {['left', 'right'].map((side) => (
          <div key={side} data-testid={`resources-${side}`} style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
            {RESOURCE_GROUPS.filter((g) => g.side === side).map(section)}
          </div>
        ))}
      </div>
      {shown.length === 0 && (
        <div style={{ textAlign: 'center', color: '#bbb', padding: 30, fontSize: 11 }}>No resources match this filter.</div>
      )}
    </div>
  );
}

/** One group (Internal engineers, Interns, MENA, South Africa, Contractors): a heading and its table. */
function ResourceSection({ group, rows, onOpen }) {
  return (
    <section
      data-testid={`resources-group-${group.key}`}
      aria-label={group.label}
      style={{ background: '#fff', border: '1px solid #ECECEC', borderRadius: 10, overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          padding: '9px 12px',
          borderBottom: '1px solid #ECECEC',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 11, fontWeight: 800, color: GREEN, letterSpacing: 1, textTransform: 'uppercase' }}>
          {group.label}
        </h3>
        <span style={{ fontSize: 10, color: '#999', fontWeight: 600 }}>{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: '14px 12px', fontSize: 10.5, color: '#bbb' }}>No one here yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={TH}>Resource</th>
                <th style={TH}>Discipline</th>
                <th style={TH}>Location</th>
                <th style={TH}>Current project</th>
                <th style={TH}>Future project</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id || r.name}>
                  <td style={TD}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {onOpen ? (
                        <button
                          type="button"
                          onClick={() => onOpen(r)}
                          title="Open this engineer"
                          style={{
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            fontFamily: 'Montserrat',
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#0D0D0D',
                            textAlign: 'left',
                            textDecoration: 'underline',
                            textDecorationColor: '#C8D3C3',
                          }}
                        >
                          {r.name}
                        </button>
                      ) : (
                        <span style={{ fontWeight: 700 }}>{r.name}</span>
                      )}
                      {r.region !== '-' && <Tag text={r.region} tone={REGION_TONE[r.region]} />}
                      <Tag text={r.employment} tone={EMPLOYMENT_TONE[r.employment]} />
                    </div>
                    {r.title && <div style={{ fontSize: 9.5, color: '#888', marginTop: 2 }}>{r.title}</div>}
                  </td>
                  {/* Through the shared label, not raw + capitalize: a blank
                      reads as Process here exactly as it does on the card. */}
                  <td style={TD}>{disciplineLabel(r.discipline)}</td>
                  {/* The card's Location. The calendar writes today's block
                      Location onto the card, so this is where they are now
                      when the block says, and their usual base when it does not. */}
                  <td style={TD}>{r.location || <span style={{ color: '#C9C9C9' }}>-</span>}</td>
                  <td style={TD}>
                    <Assignment run={r.current} />
                  </td>
                  <td style={TD}>
                    <Assignment run={r.next} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
